type SiemaConfig = { selector: string | HTMLElement; duration: number; easing: string; perPage: number | { [index: number]: number}; startIndex: number; draggable: boolean; multipleDrag: boolean; threshold: number; loop: boolean; rtl: boolean; onInit: () => void; onChange: () => void; };

export default class Siema {
    config: SiemaConfig; 
    rootElement: HTMLElement;
    selectorWidth: number;
    innerElements: Element[];
    currentSlide: number;
    perPage: number = 1;
    pointerDown?: boolean;
    drag: { startX: number; endX: number; startY: number; letItGo: boolean | null; preventClick: boolean; } = { startX: 0, endX: 0, startY: 0, letItGo: false, preventClick: false };
    sliderFrame: HTMLDivElement;
    static readonly settings: SiemaConfig = {
        selector: ".siema",
        duration: 200,
        easing: "ease-out",
        perPage: 1,
        startIndex: 0,
        draggable: true,
        multipleDrag: true,
        threshold: 20,
        loop: false,
        rtl: false,
        onInit: () => { },
        onChange: () => { },
    };

    constructor(options: Partial<SiemaConfig>) {
        // Merge defaults with user's settings
        this.config = Siema.mergeSettings(options);

        // Resolve selector's type
        const elem =
            typeof this.config.selector === "string"
                ? document.querySelector<HTMLElement>(this.config.selector)
                : this.config.selector;

        // Early throw if selector doesn't exists
        if (elem === null) {
            throw new Error("Something wrong with your selector 😭");
        }

        this.rootElement = elem;

        // update perPage number dependable of user value
        this.resolveSlidesNumber();

        // Create global references
        this.sliderFrame = document.createElement("div");
        this.selectorWidth = this.rootElement.offsetWidth;
        this.innerElements = [].slice.call(this.rootElement.children);
        this.currentSlide = this.config.loop
            ? this.config.startIndex % this.innerElements.length
            : Math.max(
                0,
                Math.min(
                    this.config.startIndex,
                    this.innerElements.length - this.perPage
                )
            );

        // Build markup and apply required styling to elements
        this.init();
    }

    static mergeSettings(options: Partial<SiemaConfig>) {
        return {...Siema.settings, ...options};
    }

    /**
     * Attaches listeners to required events.
     */
    attachEvents() {
        // Resize element on window resize
        window.addEventListener("resize", this.resizeHandler);

        // If element is draggable / swipable, add event handlers
        if (this.config.draggable) {
            // Keep track pointer hold and dragging distance
            this.pointerDown = false;
            this.drag = {
                startX: 0,
                endX: 0,
                startY: 0,
                letItGo: null,
                preventClick: false,
            };

            // Touch events
            this.rootElement.addEventListener("touchstart", this.touchstartHandler);
            this.rootElement.addEventListener("touchend", this.touchendHandler);
            this.rootElement.addEventListener("touchmove", this.touchmoveHandler);

            // Mouse events
            this.rootElement.addEventListener("mousedown", this.mousedownHandler);
            this.rootElement.addEventListener("mouseup", this.mouseupHandler);
            this.rootElement.addEventListener("mouseleave", this.mouseleaveHandler);
            this.rootElement.addEventListener("mousemove", this.mousemoveHandler);

            // Click
            this.rootElement.addEventListener("click", this.clickHandler);
        }
    }

    /**
     * Detaches listeners from required events.
     */
    detachEvents() {
        window.removeEventListener("resize", this.resizeHandler);
        this.rootElement.removeEventListener("touchstart", this.touchstartHandler);
        this.rootElement.removeEventListener("touchend", this.touchendHandler);
        this.rootElement.removeEventListener("touchmove", this.touchmoveHandler);
        this.rootElement.removeEventListener("mousedown", this.mousedownHandler);
        this.rootElement.removeEventListener("mouseup", this.mouseupHandler);
        this.rootElement.removeEventListener("mouseleave", this.mouseleaveHandler);
        this.rootElement.removeEventListener("mousemove", this.mousemoveHandler);
        this.rootElement.removeEventListener("click", this.clickHandler);
    }

    /**
     * Builds the markup and attaches listeners to required events.
     */
    init() {
        this.attachEvents();

        // hide everything out of selector's boundaries
        this.rootElement.style.overflow = "hidden";

        // rtl or ltr
        this.rootElement.style.direction = this.config.rtl ? "rtl" : "ltr";

        // build a frame and slide to a currentSlide
        this.buildSliderFrame();

        this.config.onInit.call(this);
    }

    /**
     * Build a sliderFrame and slide to a current item.
     */
    buildSliderFrame() {
        const widthItem = this.selectorWidth / this.perPage;
        const itemsToBuild = this.config.loop
            ? this.innerElements.length + 2 * this.perPage
            : this.innerElements.length;

        // Create frame and apply styling
        this.sliderFrame = document.createElement("div");
        this.sliderFrame.style.width = `${widthItem * itemsToBuild}px`;
        this.sliderFrame.style.display = "flex";
        this.enableTransition();

        if (this.config.draggable) {
            this.rootElement.style.cursor = "-webkit-grab";
        }

        // Create a document fragment to put slides into it
        const docFragment = document.createDocumentFragment();

        // Loop through the slides, add styling and add them to document fragment
        if (this.config.loop) {
            for (
                let i = this.innerElements.length - this.perPage;
                i < this.innerElements.length;
                i++
            ) {
                const element = this.buildSliderFrameItem(
                    this.innerElements[i].cloneNode(true)
                );
                docFragment.appendChild(element);
            }
        }
        for (let i = 0; i < this.innerElements.length; i++) {
            const element = this.buildSliderFrameItem(this.innerElements[i]);
            docFragment.appendChild(element);
        }
        if (this.config.loop) {
            for (let i = 0; i < this.perPage; i++) {
                const element = this.buildSliderFrameItem(
                    this.innerElements[i].cloneNode(true)
                );
                docFragment.appendChild(element);
            }
        }

        // Add fragment to the frame
        this.sliderFrame.appendChild(docFragment);

        // Clear selector (just in case something is there) and insert a frame
        this.rootElement.innerHTML = "";
        this.rootElement.appendChild(this.sliderFrame);

        // Go to currently active slide after initial build
        this.slideToCurrent();
    }

    buildSliderFrameItem(elm: Node) {
        const elementContainer = document.createElement("div");
        elementContainer.style.cssFloat = this.config.rtl ? "right" : "left";
        elementContainer.style.float = this.config.rtl ? "right" : "left";
        elementContainer.style.width = `${this.config.loop
                ? 100 / (this.innerElements.length + this.perPage * 2)
                : 100 / this.innerElements.length
            }%`;
        elementContainer.appendChild(elm);
        return elementContainer;
    }

    /**
     * Determinates slides number accordingly to clients viewport.
     */
    resolveSlidesNumber() {
        if (typeof this.config.perPage === "number") {
            this.perPage = this.config.perPage;
        } else if (typeof this.config.perPage === "object") {
            this.perPage = 1;
            const sizes: number[] = Object.keys(this.config.perPage).map(Number);

            for (const viewport of sizes) {
                if (window.innerWidth >= viewport) {
                    this.perPage = this.config.perPage[viewport];
                }
            }
        }
    }

    /**
     * Go to previous slide.
     */
    prev(howManySlides = 1, callback?: () => void) {
        // early return when there is nothing to slide
        if (this.innerElements.length <= this.perPage) {
            return;
        }

        const beforeChange = this.currentSlide;

        if (this.config.loop) {
            const isNewIndexClone = this.currentSlide - howManySlides < 0;
            if (isNewIndexClone) {
                this.disableTransition();

                const mirrorSlideIndex = this.currentSlide + this.innerElements.length;
                const mirrorSlideIndexOffset = this.perPage;
                const moveTo = mirrorSlideIndex + mirrorSlideIndexOffset;
                const offset =
                    (this.config.rtl ? 1 : -1) *
                    moveTo *
                    (this.selectorWidth / this.perPage);
                const dragDistance = this.config.draggable
                    ? this.drag.endX - this.drag.startX
                    : 0;
                this.sliderFrame.style.transform = `translate3d(${offset + dragDistance}px, 0, 0)`;
                this.currentSlide = mirrorSlideIndex - howManySlides;
            } else {
                this.currentSlide = this.currentSlide - howManySlides;
            }
        } else {
            this.currentSlide = Math.max(this.currentSlide - howManySlides, 0);
        }

        if (beforeChange !== this.currentSlide) {
            this.slideToCurrent(this.config.loop);
            this.config.onChange.call(this);
            if (callback) {
                callback.call(this);
            }
        }
    }

    next(howManySlides = 1, callback?: () => void) {
        // early return when there is nothing to slide
        if (this.innerElements.length <= this.perPage) {
            return;
        }

        const beforeChange = this.currentSlide;

        if (this.config.loop) {
            const isNewIndexClone =
                this.currentSlide + howManySlides >
                this.innerElements.length - this.perPage;
            if (isNewIndexClone) {
                this.disableTransition();

                const mirrorSlideIndex = this.currentSlide - this.innerElements.length;
                const mirrorSlideIndexOffset = this.perPage;
                const moveTo = mirrorSlideIndex + mirrorSlideIndexOffset;
                const offset =
                    (this.config.rtl ? 1 : -1) *
                    moveTo *
                    (this.selectorWidth / this.perPage);
                const dragDistance = this.config.draggable
                    ? this.drag.endX - this.drag.startX
                    : 0;

                this.sliderFrame.style.transform = `translate3d(${offset + dragDistance}px, 0, 0)`;
                this.currentSlide = mirrorSlideIndex + howManySlides;
            } else {
                this.currentSlide = this.currentSlide + howManySlides;
            }
        } else {
            this.currentSlide = Math.min(
                this.currentSlide + howManySlides,
                this.innerElements.length - this.perPage
            );
        }
        if (beforeChange !== this.currentSlide) {
            this.slideToCurrent(this.config.loop);
            this.config.onChange.call(this);
            if (callback) {
                callback.call(this);
            }
        }
    }

    /**
     * Disable transition on sliderFrame.
     */
    disableTransition() {
        this.sliderFrame.style.transition = `all 0ms ${this.config.easing}`;
    }

    /**
     * Enable transition on sliderFrame.
     */
    enableTransition() {
        this.sliderFrame.style.transition = `all ${this.config.duration}ms ${this.config.easing}`;
    }

    /**
     * Go to slide with particular index
     * @param {number} index - Item index to slide to.
     * @param {function} callback - Optional callback function.
     */
    goTo(index: number, callback: () => void) {
        if (this.innerElements.length <= this.perPage) {
            return;
        }
        const beforeChange = this.currentSlide;
        this.currentSlide = this.config.loop
            ? index % this.innerElements.length
            : Math.min(Math.max(index, 0), this.innerElements.length - this.perPage);
        if (beforeChange !== this.currentSlide) {
            this.slideToCurrent();
            this.config.onChange.call(this);
            if (callback) {
                callback.call(this);
            }
        }
    }

    /**
     * Moves sliders frame to position of currently active slide
     */
    slideToCurrent(enableTransition?: boolean  ) {
        const currentSlide = this.config.loop
            ? this.currentSlide + this.perPage
            : this.currentSlide;
        const offset =
            (this.config.rtl ? 1 : -1) *
            currentSlide *
            (this.selectorWidth / this.perPage);

        if (enableTransition) {
            // This one is tricky, I know but this is a perfect explanation:
            // https://youtu.be/cCOL7MC4Pl0
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.enableTransition();
                    this.sliderFrame.style.transform = `translate3d(${offset}px, 0, 0)`;
                });
            });
        } else {
            this.sliderFrame.style.transform = `translate3d(${offset}px, 0, 0)`;
        }
    }

    /**
     * Recalculate drag /swipe event and reposition the frame of a slider
     */
    updateAfterDrag() {
        const movement = (this.config.rtl ? -1 : 1) * (this.drag.endX - this.drag.startX);
        const movementDistance = Math.abs(movement);
        const howManySliderToSlide = this.config.multipleDrag
            ? Math.ceil(movementDistance / (this.selectorWidth / this.perPage))
            : 1;

        const slideToNegativeClone =
            movement > 0 && this.currentSlide - howManySliderToSlide < 0;
        const slideToPositiveClone =
            movement < 0 &&
            this.currentSlide + howManySliderToSlide >
            this.innerElements.length - this.perPage;

        if (
            movement > 0 &&
            movementDistance > this.config.threshold &&
            this.innerElements.length > this.perPage
        ) {
            this.prev(howManySliderToSlide);
        } else if (
            movement < 0 &&
            movementDistance > this.config.threshold &&
            this.innerElements.length > this.perPage
        ) {
            this.next(howManySliderToSlide);
        }
        this.slideToCurrent(slideToNegativeClone || slideToPositiveClone);
    }

    /**
     * When window resizes, resize slider components as well
     */
    resizeHandler = () => {
        // update perPage number dependable of user value
        this.resolveSlidesNumber();

        // relcalculate currentSlide
        // prevent hiding items when browser width increases
        if (this.currentSlide + this.perPage > this.innerElements.length) {
            this.currentSlide =
                this.innerElements.length <= this.perPage
                    ? 0
                    : this.innerElements.length - this.perPage;
        }

        this.selectorWidth = this.rootElement.offsetWidth;

        this.buildSliderFrame();
    }

    /**
     * Clear drag after touchend and mouseup event
     */
    clearDrag() {
        this.drag = {
            startX: 0,
            endX: 0,
            startY: 0,
            letItGo: null,
            preventClick: this.drag.preventClick,
        };
    }

    /**
     * touchstart event handler
     */
    touchstartHandler = (e: TouchEvent) => {
        if (!e.target || !(e.target instanceof HTMLElement)) return;

        // Prevent dragging / swiping on inputs, selects and textareas
        const ignoreSiema =
            ["TEXTAREA", "OPTION", "INPUT", "SELECT"].indexOf(e.target.nodeName) !== -1;
        if (ignoreSiema) {
            return;
        }

        e.stopPropagation();
        this.pointerDown = true;
        this.drag.startX = e.touches[0].pageX;
        this.drag.startY = e.touches[0].pageY;
    }

    /**
     * touchend event handler
     */
    touchendHandler = (e: TouchEvent) => {
        e.stopPropagation();
        this.pointerDown = false;
        this.enableTransition();
        if (this.drag.endX) {
            this.updateAfterDrag();
        }
        this.clearDrag();
    }

    /**
     * touchmove event handler
     */
    touchmoveHandler = (e: TouchEvent) => {
        e.stopPropagation();

        if (this.drag.letItGo === null) {
            this.drag.letItGo =
                Math.abs(this.drag.startY - e.touches[0].pageY) <
                Math.abs(this.drag.startX - e.touches[0].pageX);
        }

        if (this.pointerDown && this.drag.letItGo) {
            e.preventDefault();
            this.drag.endX = e.touches[0].pageX;
            this.sliderFrame.style.transition = `all 0ms ${this.config.easing}`;

            const currentSlide = this.config.loop
                ? this.currentSlide + this.perPage
                : this.currentSlide;
            const currentOffset = currentSlide * (this.selectorWidth / this.perPage);
            const dragOffset = this.drag.endX - this.drag.startX;
            const offset = this.config.rtl
                ? currentOffset + dragOffset
                : currentOffset - dragOffset;

            this.sliderFrame.style.transform = `translate3d(${(this.config.rtl ? 1 : -1) * offset}px, 0, 0)`;
        }
    }

    /**
     * mousedown event handler
     */
    mousedownHandler = (e: MouseEvent) => {
        if (!e.target || !(e.target instanceof Element)) return;
        // Prevent dragging / swiping on inputs, selects and textareas
        const ignoreSiema =
            ["TEXTAREA", "OPTION", "INPUT", "SELECT"].indexOf(e.target.nodeName) !== -1;
        if (ignoreSiema) {
            return;
        }

        e.preventDefault();
        e.stopPropagation();
        this.pointerDown = true;
        this.drag.startX = e.pageX;
    }

    /**
     * mouseup event handler
     */
    mouseupHandler = (e: MouseEvent) => {
        e.stopPropagation();
        this.pointerDown = false;
        this.rootElement.style.cursor = "-webkit-grab";
        this.enableTransition();
        if (this.drag.endX) {
            this.updateAfterDrag();
        }
        this.clearDrag();
    }

    /**
     * mousemove event handler
     */
    mousemoveHandler = (e: MouseEvent) => {
        if(!e.target || !(e.target instanceof Element)) return;

        e.preventDefault();

        if (this.pointerDown) {
            // if dragged element is a link
            // mark preventClick prop as a true
            // to detemine about browser redirection later on
            if (e.target.nodeName === "A") {
                this.drag.preventClick = true;
            }

            this.drag.endX = e.pageX;
            this.rootElement.style.cursor = "-webkit-grabbing";
            
            this.sliderFrame.style.webkitTransition = `all 0ms ${this.config.easing}`;
            this.sliderFrame.style.transition = `all 0ms ${this.config.easing}`;

            const currentSlide = this.config.loop
                ? this.currentSlide + this.perPage
                : this.currentSlide;
            const currentOffset = currentSlide * (this.selectorWidth / this.perPage);
            const dragOffset = this.drag.endX - this.drag.startX;
            const offset = this.config.rtl
                ? currentOffset + dragOffset
                : currentOffset - dragOffset;
            this.sliderFrame.style.transform = `translate3d(${(this.config.rtl ? 1 : -1) * offset
                }px, 0, 0)`;
        }
    }

    /**
     * mouseleave event handler
     */
    mouseleaveHandler = (e: MouseEvent) => {
        if (this.pointerDown) {
            this.pointerDown = false;
            this.rootElement.style.cursor = "-webkit-grab";
            this.drag.endX = e.pageX;
            this.drag.preventClick = false;
            this.enableTransition();
            this.updateAfterDrag();
            this.clearDrag();
        }
    }

    /**
     * click event handler
     */
    clickHandler = (e: MouseEvent) => {
        // if the dragged element is a link
        // prevent browsers from folowing the link
        if (this.drag.preventClick) {
            e.preventDefault();
        }
        this.drag.preventClick = false;
    }

    remove(index: number, callback?: () => void) {
        if (index < 0 || index >= this.innerElements.length) {
            throw new Error("Item to remove doesn't exist 😭");
        }

        // Shift sliderFrame back by one item when:
        // 1. Item with lower index than currenSlide is removed.
        // 2. Last item is removed.
        const lowerIndex = index < this.currentSlide;
        const lastItem = this.currentSlide + this.perPage - 1 === index;

        if (lowerIndex || lastItem) {
            this.currentSlide--;
        }

        this.innerElements.splice(index, 1);

        // build a frame and slide to a currentSlide
        this.buildSliderFrame();

        if (callback) {
            callback.call(this);
        }
    }

    insert(item: HTMLElement, index: number, callback?: () => void) {
        if (index < 0 || index > this.innerElements.length + 1) {
            throw new Error("Unable to inset it at this index 😭");
        }
        if (this.innerElements.indexOf(item) !== -1) {
            throw new Error("The same item in a carousel? Really? Nope 😭");
        }

        // Avoid shifting content
        const shouldItShift = index <= this.currentSlide && this.currentSlide > 0 && this.innerElements.length;
        this.currentSlide = shouldItShift ? this.currentSlide + 1 : this.currentSlide;

        this.innerElements.splice(index, 0, item);

        // build a frame and slide to a currentSlide
        this.buildSliderFrame();

        if (callback) {
            callback.call(this);
        }
    }

    prepend(item: HTMLElement, callback?: () => void) {
        this.insert(item, 0);
        if (callback) {
            callback.call(this);
        }
    }

    append(item: HTMLElement, callback?: () => void) {
        this.insert(item, this.innerElements.length + 1);
        if (callback) {
            callback.call(this);
        }
    }

    destroy(restoreMarkup = false, callback?: () => void) {
        this.detachEvents();

        this.rootElement.style.cursor = "auto";

        if (restoreMarkup) {
            const slides = document.createDocumentFragment();
            for (let i = 0; i < this.innerElements.length; i++) {
                slides.appendChild(this.innerElements[i]);
            }
            this.rootElement.innerHTML = "";
            this.rootElement.appendChild(slides);
            this.rootElement.removeAttribute("style");
        }

        if (callback) {
            callback.call(this);
        }
    }
}
